import type {
  AnnuaireDuServeur,
  FriendRequest,
  FriendSuggestion,
  FriendshipStatus,
  GeoServer,
  PublicUser,
} from '@sirocco/shared';
import { onValue, ref } from 'firebase/database';
import { db } from './app';
import { requireUid } from './auth';
import { ensureDirect, noterAmitieRompue } from './messages';
import { estCompteOfficiel } from './officiel';
import { normalizeUsername, paths } from './paths';
import { lireLesParcours } from './parcours';
import { loadUsers, toIso, toPublicUser, unknownUser, type UserRecord } from './records';
import {
  readKeys,
  readList,
  readMany,
  readOptionalKeys,
  readOptionalValue,
  readValue,
  SiroccoDataError,
  updateValues,
  writeValue,
} from './rtdb';
import { langue, t } from '../i18n';
import { versListe } from '../utils/langues';

/**
 * Amis, demandes et suggestions.
 *
 * L'amitie est un lien double : elle s'ecrit chez les deux membres, sinon
 * l'un des deux ne verrait jamais l'autre dans sa liste. Une demande, elle,
 * laisse deux traces : chez celui qui recoit (`friendRequests`) et chez
 * celui qui envoie (`sentRequests`), pour savoir sans requete croisee ou en
 * est la relation.
 */

/** Une demande, telle qu'elle est ecrite dans `friendRequests/{uid}/{id}`. */
interface FriendRequestRecord {
  fromId: string;
  mutualFriends: number;
  createdAt: number;
}

/** Au-dela de ce delai, un compte n'est plus un nouvel arrivant. */
const NEWCOMER_WINDOW_MS = 30 * 24 * 60 * 60 * 1000;

/**
 * Nombre de suggestions affichees sur l'ecran "Amis".
 *
 * Quinze depuis le 13 septembre 2026, contre dix : une ville qui grandit
 * offre plus de visages qu'une seule poignee, et la liste se lit toujours
 * d'un coup de pouce.
 */
const SUGGESTION_LIMIT = 20;

/** Mes relations, en quatre lectures : de quoi statuer sur n'importe qui. */
interface Circle {
  friends: Set<string>;
  sent: Set<string>;
  incoming: Set<string>;
  blocked: Set<string>;
  /** Les comptes masques : on ne me les suggere plus, sans rien leur fermer. */
  hidden: Set<string>;
}

export async function loadCircle(uid: string): Promise<Circle> {
  const [friends, sent, requests, blocked, hidden] = await Promise.all([
    readKeys(paths.friends(uid)),
    readKeys(paths.sentRequests(uid)),
    readValue<Record<string, FriendRequestRecord>>(paths.friendRequests(uid)),
    readOptionalKeys(paths.blockedUsers(uid)),
    readOptionalKeys(paths.hiddenUsers(uid)),
  ]);
  return {
    hidden: new Set(hidden),
    friends: new Set(friends),
    sent: new Set(sent),
    incoming: new Set(Object.values(requests ?? {}).map((request) => request.fromId)),
    blocked: new Set(blocked),
  };
}

function statusOf(circle: Circle, otherUid: string): FriendshipStatus {
  if (circle.friends.has(otherUid)) return 'friends';
  if (circle.sent.has(otherUid)) return 'requested';
  if (circle.incoming.has(otherUid)) return 'incoming';
  return 'none';
}

/**
 * La phrase affichee sous un profil suggere : l'arrivee recente donne une
 * raison de faire le premier pas. Sinon, rien.
 */
function reasonFor(record: UserRecord): string {
  const isNewcomer = Date.now() - (record.createdAt ?? 0) < NEWCOMER_WINDOW_MS;
  return isNewcomer ? t.messages.amis.nouveau : '';
}

export async function friendshipStatus(otherUid: string): Promise<FriendshipStatus> {
  const uid = requireUid();
  const circle = await loadCircle(uid);
  return statusOf(circle, otherUid);
}

/** Les identifiants de mes amis, sans leurs fiches : de quoi reconnaitre un proche. */
export async function listFriendIds(): Promise<string[]> {
  return readKeys(paths.friends(requireUid()));
}

/**
 * Ceux que j'ai croises a une sortie dans cette ville.
 *
 * Le moteur de points tient les rencontres des deux cotes ; une branche
 * absente veut seulement dire que je n'ai encore croise personne.
 */
export async function listMetIds(serverId: string): Promise<string[]> {
  return readOptionalKeys(paths.rencontres(serverId, requireUid()));
}

export async function listFriends(): Promise<PublicUser[]> {
  const uid = requireUid();
  const ids = await readKeys(paths.friends(uid));
  if (ids.length === 0) return [];

  const users = await loadUsers(ids);
  return ids
    .map((id) => users.get(id) ?? unknownUser(id))
    .sort((a, b) => a.username.localeCompare(b.username, 'fr'));
}

/**
 * Les demandes recues, la plus recente en tete.
 *
 * Deux traces peuvent viser la meme personne : l'identifiant d'une
 * demande a d'abord ete une cle Firebase avant de devenir l'uid de son
 * expediteur, et rien n'empechait alors d'en envoyer deux. On ne garde
 * que la plus recente, et on ecarte celles dont l'expediteur est deja un
 * ami — repondre a une demande devenue sans objet n'a aucun sens.
 *
 * Un membre bloque est ecarte lui aussi : les regles lui interdisent
 * desormais d'ecrire ici, mais une demande partie avant le blocage y
 * dort encore, et la faire remonter reviendrait a le laisser insister.
 */
/**
 * Combien de demandes je n'ai pas encore vues.
 *
 * « Vues » et non « traitees » : ouvrir l'ecran suffit. Une pastille dit
 * qu'il y a quelque chose de neuf derriere, pas qu'il reste du travail —
 * laisser une demande sans reponse est un choix, et la pastille n'a pas a
 * le rappeler indefiniment.
 *
 * Volontairement pauvre : elle ne charge aucun profil. Elle est relue a
 * chaque changement d'onglet.
 */
/**
 * Prevenir des qu'une demande d'ami arrive ou disparait.
 *
 * La pastille de la barre d'onglets en compte trois sortes ; celle-ci est
 * la seule qui ne vive pas dans les fils de discussion, donc la seule
 * qu'il faut ecouter a part.
 */
export function watchRequests(onChange: () => void): () => void {
  return onValue(ref(db(), paths.friendRequests(requireUid())), () => onChange());
}

export async function countNewRequests(): Promise<number> {
  const uid = requireUid();
  const [demandes, vuA] = await Promise.all([
    readValue<Record<string, FriendRequestRecord>>(paths.friendRequests(uid)),
    readOptionalValue<number>(`${paths.user(uid)}/seenFriendRequestsAt`),
  ]);
  const depuis = vuA ?? 0;
  return Object.values(demandes ?? {}).filter((d) => (d.createdAt ?? 0) > depuis).length;
}

/** L'ecran a ete ouvert : ce qui s'y trouvait est vu. */
export async function markRequestsSeen(): Promise<void> {
  const uid = requireUid();
  await writeValue(`${paths.user(uid)}/seenFriendRequestsAt`, Date.now());
}

export async function listRequests(): Promise<FriendRequest[]> {
  const uid = requireUid();
  const [rows, circle] = await Promise.all([
    readList<FriendRequestRecord>(paths.friendRequests(uid), { orderBy: 'createdAt' }),
    loadCircle(uid),
  ]);
  if (rows.length === 0) return [];

  const users = await loadUsers(rows.map((row) => row.fromId));
  const requests: FriendRequest[] = [];
  const seen = new Set<string>();

  for (const row of [...rows].reverse()) {
    if (
      !row.fromId ||
      circle.friends.has(row.fromId) ||
      circle.blocked.has(row.fromId) ||
      seen.has(row.fromId)
    ) {
      continue;
    }
    seen.add(row.fromId);
    requests.push({
      id: row.id,
      from: users.get(row.fromId) ?? unknownUser(row.fromId),
      mutualFriends: row.mutualFriends ?? 0,
      createdAt: toIso(row.createdAt),
    });
  }
  return requests;
}

/**
 * Qui rencontrer sur ce spot.
 * La base ne sait pas filtrer sur `currentServerId` : on lit l'annuaire une
 * fois et on trie en memoire, ce qui evite une lecture par membre.
 */
export async function listSuggestions(serverId: string, tour = 0): Promise<FriendSuggestion[]> {
  const uid = requireUid();
  const [records, circle, croises] = await Promise.all([
    readList<UserRecord>(paths.users()),
    loadCircle(uid),
    /*
      Ceux que j'ai vraiment croises, atteste par un tiers : la
      billetterie produit un fait rare — X et Y etaient au meme endroit —
      et il vaut mieux que n'importe quelle suggestion par ville.
    */
    readOptionalValue<Record<string, { dernierEvent?: string; premiereFois?: number }>>(
      paths.rencontres(serverId, uid),
    ),
  ]);

  const rencontres = croises ?? {};

  const classes = (
    records
      .filter(
        (record) =>
          record.id !== uid &&
          record.currentServerId === serverId &&
          // On ne rencontre pas le compte de l'application a une sortie.
          record.officiel !== true &&
          !circle.friends.has(record.id) &&
          !circle.blocked.has(record.id) &&
          // Un compte masque ne revient pas par les suggestions.
          !circle.hidden.has(record.id),
      )
      /*
        Ceux qu'on a croises passent devant, les plus recents d'abord.
        Le reste suit, par niveau — ce sont les membres actifs, faute de
        mieux a dire d'eux.
      */
      .sort((a, b) => {
        const vuA = rencontres[a.id]?.premiereFois ?? 0;
        const vuB = rencontres[b.id]?.premiereFois ?? 0;
        if (vuA !== vuB) return vuB - vuA;
        return (b.level ?? 1) - (a.level ?? 1);
      })
  );

  /*
    On ne coupe plus les premiers du classement : on tourne dans le vivier.

    Tirer la liste vers le bas avance d'une fenetre entiere, et repart au
    debut une fois le bout atteint. Trente membres pour vingt places, c'est
    dix visages neufs et dix gardes — de la nouveaute sans repartir de
    zero, et tout le monde finit par passer.

    La fenetre garde toujours sa taille : on prend vingt personnes, ou
    tout le monde s'il y en a moins. Un spot qui tient dans une seule
    fenetre ne peut evidemment montrer personne d'autre — l'ordre change,
    et c'est tout ce qu'on peut faire d'honnete.
  */
  if (classes.length === 0) return [];
  /*
    Le pas, c'est une fenetre entiere quand il y a de quoi renouveler.

    Sinon un seul cran : avec un vivier de vingt personnes tout rond, un
    pas de vingt retomberait chaque fois sur le meme depart, et tirer la
    liste ne changerait rien du tout. Un cran fait au moins tourner
    l'ordre.
  */
  const pas = classes.length > SUGGESTION_LIMIT ? SUGGESTION_LIMIT : 1;
  const depart = (((tour * pas) % classes.length) + classes.length) % classes.length;

  return Array.from({ length: Math.min(SUGGESTION_LIMIT, classes.length) }, (_, rang) => {
    const record = classes[(depart + rang) % classes.length];
    return {
      user: toPublicUser(record.id, record),
      reason: rencontres[record.id] ? t.messages.amis.croise : reasonFor(record),
      status: statusOf(circle, record.id),
    };
  });
}

/**
 * L'annuaire d'un serveur : tous ses membres, par ordre alphabetique.
 *
 * Qui en fait partie, c'est sa liste quand il est prive — ceux qui ont la
 * cle, qu'ils y soient deja entres ou non : on cherche justement celui
 * qu'on n'a pas encore croise. Sinon, tous ceux qui y sont passes.
 *
 * On n'y voit ni soi, ni le compte de l'application, ni ceux qu'on a
 * bloques ou masques, ni celui qui a reserve son profil a ses amis sans
 * etre des miens : l'annuaire ne montre rien que son profil ne montrerait
 * pas.
 */
export async function listerLAnnuaire(serverId: string): Promise<AnnuaireDuServeur> {
  const uid = requireUid();
  const [server, circle, cv] = await Promise.all([
    readValue<GeoServer>(paths.server(serverId)),
    loadCircle(uid),
    lireLesParcours(serverId),
  ]);
  const catalogue = server?.formations ?? {};
  // Une formation retiree du catalogue ne range plus personne.
  const connue = (id: string | null | undefined) => (id && catalogue[id] ? id : undefined);

  let records: { id: string; record: UserRecord }[];
  if (server?.prive) {
    const membres = await readOptionalKeys(paths.serveurPriveListe(serverId));
    const lus = await readMany<UserRecord>(paths.user, membres);
    records = [...lus].map(([id, record]) => ({ id, record }));
  } else {
    records = (await readList<UserRecord>(paths.users()))
      .filter((record) => record.currentServerId === serverId || record.destinations?.[serverId])
      .map((record) => ({ id: record.id, record }));
  }

  const membres = records
    .filter(
      ({ id, record }) =>
        id !== uid &&
        record.officiel !== true &&
        !circle.blocked.has(id) &&
        !circle.hidden.has(id) &&
        (record.privacy?.profileVisibility !== 'friends' || circle.friends.has(id)),
    )
    .map(({ id, record }) => {
      const formation = connue(cv[id]?.formation);
      return {
        user: toPublicUser(id, record),
        langues: versListe(record.languesParlees),
        status: statusOf(circle, id),
        ...(formation ? { formation } : {}),
      };
    })
    .sort((a, b) => a.user.username.localeCompare(b.user.username, langue, { sensitivity: 'base' }));

  /* La cle departage deux formations posees au meme rang. */
  const formations = Object.entries(catalogue)
    .sort(([cleA, a], [cleB, b]) => a.ordre - b.ordre || cleA.localeCompare(cleB))
    .map(([id, formation]) => ({ id, nom: formation.nom }));

  return {
    membres,
    formations,
    maFormation: connue(cv[uid]?.formation) ?? null,
  };
}

/**
 * Tous les comptes de l'application, par ordre alphabetique.
 *
 * Pour l'administrateur : l'ecran d'ajout ne la demande qu'a lui, a la
 * place des suggestions de son spot. Elle ne lit rien de plus que la
 * recherche, qui parcourt deja tous les comptes. Personne n'en est
 * ecarte, ni un compte bloque ou masque, ni un profil reserve aux amis :
 * on administre aussi ceux qu'on ne veut plus voir. Seul un compte sans
 * pseudo, inscription laissee en route, n'a rien a montrer.
 */
export async function listerTousLesMembres(): Promise<FriendSuggestion[]> {
  const uid = requireUid();
  const [records, circle] = await Promise.all([
    readList<UserRecord>(paths.users()),
    loadCircle(uid),
  ]);

  return records
    .filter((record) => record.id !== uid && (record.username ?? '').trim())
    .map((record) => ({
      user: toPublicUser(record.id, record),
      reason: reasonFor(record),
      status: statusOf(circle, record.id),
    }))
    .sort((a, b) => a.user.username.localeCompare(b.user.username, langue, { sensitivity: 'base' }));
}

/**
 * Recherche par pseudo.
 * On passe par la meme normalisation que l'index des pseudos : "Léa" et
 * "lea" doivent se trouver l'un l'autre.
 */
export async function searchUsers(search: string): Promise<FriendSuggestion[]> {
  const uid = requireUid();
  const needle = normalizeUsername(search);
  if (!needle) return [];

  const [records, circle] = await Promise.all([
    readList<UserRecord>(paths.users()),
    loadCircle(uid),
  ]);

  return records
    .filter(
      (record) =>
        record.id !== uid &&
        !circle.blocked.has(record.id) &&
        normalizeUsername(record.username ?? '').includes(needle),
    )
    .map((record) => ({
      user: toPublicUser(record.id, record),
      reason: reasonFor(record),
      status: statusOf(circle, record.id),
    }));
}

/**
 * Demande d'ami, ou annulation.
 * Le bouton est le meme des deux cotes de l'action : un second appui retire
 * la demande, chez moi comme chez l'autre, pour ne pas laisser une demande
 * fantome dans sa liste.
 */
export async function requestFriend(otherUid: string, serverId: string): Promise<FriendshipStatus> {
  const uid = requireUid();
  if (otherUid === uid) {
    throw new SiroccoDataError(t.messages.amis.soiMeme, 'self-request');
  }

  // Le compte de l'application ne se lie a personne : voir `officiel.ts`.
  if (await estCompteOfficiel(otherUid)) {
    throw new SiroccoDataError(
      t.messages.amis.compteOfficiel,
      'compte-officiel',
    );
  }

  const circle = await loadCircle(uid);
  if (circle.friends.has(otherUid)) return 'friends';

  // Demander en ami quelqu'un qu'on a bloque n'a pas de sens : le
  // debloquer d'abord est le geste attendu.
  if (circle.blocked.has(otherUid)) {
    throw new SiroccoDataError(
      t.messages.amis.bloque,
      'blocked',
    );
  }

  // Il m'a deja demande : le bouton dit "Accepter la demande", il doit
  // donc accepter. Envoyer une demande en retour laisserait deux
  // demandes croisees et aucune amitie.
  if (circle.incoming.has(otherUid)) {
    await acceptRequest(otherUid, serverId);
    return 'friends';
  }

  // Annulation. La demande porte l'identifiant de son expediteur, donc
  // on sait ou la retirer sans ouvrir la boite de reception de l'autre —
  // que les regles nous interdisent de lire, a juste titre.
  if (circle.sent.has(otherUid)) {
    await updateValues({
      [paths.sentRequest(uid, otherUid)]: null,
      [paths.friendRequest(otherUid, uid)]: null,
    });
    return 'none';
  }

  /*
    Redemander est libre.

    Rien n'est bride ici : retirer sa demande et la renvoyer marche
    autant de fois qu'on veut. C'est l'annonce qui se tait — la fonction
    qui la porte ne sonne qu'une fois par heure vers la meme personne.
    Le bruit cesse d'etre une arme, le geste garde son sens.
  */

  // Les amis en commun sont figes a l'envoi : la demande doit pouvoir
  // s'afficher sans relire les deux listes d'amis a chaque ouverture.
  const theirFriends = await readKeys(paths.friends(otherUid));
  const mutualFriends = theirFriends.filter((friendId) => circle.friends.has(friendId)).length;

  // L'identifiant de la demande est celui de son expediteur : cela la
  // rend retrouvable des deux cotes, et interdit d'en envoyer deux a la
  // meme personne.
  //
  // Les regles refusent cette ecriture quand l'autre m'a bloque, et c'est
  // le seul endroit ou on l'apprend : sa liste de blocages ne se lit pas,
  // a juste titre. Un `PERMISSION_DENIED` brut n'apprendrait rien a
  // personne, d'ou la traduction.
  try {
    await updateValues({
      [paths.sentRequest(uid, otherUid)]: true,
      [paths.friendRequest(otherUid, uid)]: {
        fromId: uid,
        mutualFriends,
        createdAt: Date.now(),
      },
    });
  } catch {
    throw new SiroccoDataError(
      t.messages.amis.refuse,
      'request-refused',
    );
  }
  return 'requested';
}

/**
 * Accepte une demande.
 *
 * Les deux sens de l'amitie et le nettoyage des traces partent dans la
 * meme ecriture : un lien a sens unique laisserait un ami invisible d'un
 * cote, et une demande envoyee oubliee chez l'expediteur lui interdirait
 * de nous redemander plus tard.
 *
 * Effacer `sentRequests/{expediteur}/{moi}` demande une regle dediee :
 * la branche appartient a l'expediteur, mais son destinataire doit
 * pouvoir en retirer sa propre ligne, sinon toute la mise a jour groupee
 * est refusee et l'acceptation echoue en silence.
 */
export async function acceptRequest(requestId: string, serverId: string): Promise<void> {
  const uid = requireUid();
  const inbox = await readInbox(uid);
  const request = inbox[requestId];
  if (!request) throw new SiroccoDataError(t.messages.amis.plusDeDemande, 'no-request');

  await updateValues({
    [paths.friend(uid, request.fromId)]: true,
    [paths.friend(request.fromId, uid)]: true,
    /*
      Ce qui previendra celui qui avait demande.

      Dans la meme passe que l'amitie, et pas apres : une mise a jour
      groupee est tout ou rien, donc soit l'amitie et son annonce
      existent ensemble, soit ni l'une ni l'autre. Une ecriture separee
      qui echoue laisserait une amitie dont personne n'est prevenu.
    */
    [paths.friendAccepted(request.fromId, uid)]: Date.now(),
    [paths.sentRequest(request.fromId, uid)]: null,
    // Une demande croisee, partie avant que celle-ci soit acceptee, n'a
    // plus lieu d'etre : les deux sont desormais amis.
    [paths.friendRequest(request.fromId, uid)]: null,
    [paths.sentRequest(uid, request.fromId)]: null,
    ...tracesOf(uid, inbox, request.fromId),
  });

  /*
    Une amitie qui se noue ouvre un fil, vide, chez les deux. Il n'a rien
    a dire encore — c'est justement ce qu'il propose. Sans lui, deux
    nouveaux amis se retrouvent devant une liste ou rien n'a change, et il
    faut passer par un profil pour se parler.

    Apres l'amitie et non avec elle : la conversation n'est qu'une
    invitation, et son echec ne doit pas empecher l'amitie.
  */
  await ensureDirect(request.fromId, serverId).catch(() => undefined);
}

/**
 * Toutes les demandes d'un meme expediteur dans ma boite, a effacer.
 * N'en retirer qu'une laisserait un doublon d'avant la convention
 * "identifiant = expediteur", et donc une demande a laquelle repondre
 * pour quelqu'un qui est deja mon ami.
 */
function tracesOf(
  uid: string,
  inbox: Record<string, FriendRequestRecord>,
  fromId: string,
): Record<string, null> {
  const updates: Record<string, null> = {};
  for (const [key, row] of Object.entries(inbox)) {
    if (row.fromId === fromId) updates[paths.friendRequest(uid, key)] = null;
  }
  return updates;
}

async function readInbox(uid: string): Promise<Record<string, FriendRequestRecord>> {
  return (await readValue<Record<string, FriendRequestRecord>>(paths.friendRequests(uid))) ?? {};
}

/**
 * Retire quelqu'un de ses amis.
 * Le lien tombe des deux cotes : le laisser chez l'autre lui donnerait un
 * ami qui ne le voit plus dans sa propre liste.
 */
export async function removeFriend(otherUid: string): Promise<void> {
  const uid = requireUid();

  /*
    Le mot avant le geste.

    Retirer quelqu'un ferme la conversation a l'ecriture : le champ
    devient inerte sans que rien ne l'explique, des deux cotes. Une ligne
    dans le fil dit ce qui s'est passe — posee avant la rupture, tant que
    tout est encore ouvert. Elle est facultative : si elle echoue, ou s'il
    n'y a jamais eu de conversation, on retire quand meme.
  */
  await noterAmitieRompue(otherUid).catch(() => undefined);

  await updateValues({
    [paths.friend(uid, otherUid)]: null,
    [paths.friend(otherUid, uid)]: null,
  });
}

/**
 * Refuse une demande.
 * On retire aussi la trace chez l'expediteur : sans elle, son bouton
 * resterait bloque sur "Demande envoyée" et il ne pourrait jamais reessayer.
 */
export async function declineRequest(requestId: string): Promise<void> {
  const uid = requireUid();
  const inbox = await readInbox(uid);
  const request = inbox[requestId];
  if (!request) return;

  await updateValues({
    [paths.sentRequest(request.fromId, uid)]: null,
    ...tracesOf(uid, inbox, request.fromId),
  });
}
